import { FC } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import dayjs from "dayjs";
import { useCreditTransactions } from "../../../../services/usePerfilPU";
import type { CreditTransaction } from "../../../../services/usePerfilPU";
import { useParams } from "react-router-dom";

interface TransactionsTableProps {
  transactions: CreditTransaction[];
}

const TransactionsTable: FC<TransactionsTableProps> = ({ transactions }) => {
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Data</TableCell>
          <TableCell>Descrição</TableCell>
          <TableCell>Feira</TableCell>
          <TableCell>Tipo</TableCell>
          <TableCell align="right">Valor</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {transactions.map((transaction: CreditTransaction) => (
          <TableRow key={transaction.id}>
            <TableCell>
              <Typography variant="body2">
                {dayjs(transaction.created_at).format("DD/MM/YYYY")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {dayjs(transaction.created_at).format("HH:mm")}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{transaction.description}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{transaction.seed_name}</Typography>
            </TableCell>
            <TableCell>
              <Chip
                label={transaction.source_type_display}
                size="small"
                variant="outlined"
              />
            </TableCell>
            <TableCell align="right">
              <Typography
                variant="body2"
                fontWeight={600}
                color={
                  parseFloat(transaction.amount) >= 0
                    ? "success.main"
                    : "error.main"
                }
              >
                {parseFloat(transaction.amount) < 0 ? "-" : ""}
                R$ {Math.abs(parseFloat(transaction.amount)).toFixed(2)}
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

interface TransactionAttributeProps {
  color?: string;
  label: string;
  value: string;
}

const TransactionAttribute: FC<TransactionAttributeProps> = ({
  color = undefined,
  label,
  value,
}) => {
  return (
    <Box sx={{ display: "flex", gap: 2, justifyContent: "space-between" }}>
      <Typography variant="subtitle2">{label}:</Typography>
      <Typography color={color} variant="body2" sx={{ textAlign: "right" }}>
        {value}
      </Typography>
    </Box>
  );
};

interface TransactionsCardsProps {
  transactions: CreditTransaction[];
}

const TransactionsCards: FC<TransactionsCardsProps> = ({ transactions }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {transactions.map((transaction: CreditTransaction) => (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            borderRadius: 3,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          <TransactionAttribute
            label="Data"
            value={dayjs(transaction.created_at).format("DD/MM/YYYY HH:mm")}
          />
          <TransactionAttribute
            label="Tipo"
            value={transaction.source_type_display}
          />
          <TransactionAttribute
            color={
              parseFloat(transaction.amount) >= 0
                ? "success.main"
                : "error.main"
            }
            label="Valor"
            value={`R$ ${Math.abs(parseFloat(transaction.amount)).toFixed(2)}`}
          />
        </Paper>
      ))}
    </Box>
  );
};

interface TransactionsModalProps {
  open: boolean;
  onClose: () => void;
}

const TransactionsModal: FC<TransactionsModalProps> = ({ open, onClose }) => {
  const { slug } = useParams();
  const { data, isLoading, error } = useCreditTransactions({ seedSlug: slug });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (!data) {
    return null;
  }

  const transactions = data.results;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <DialogTitle>
        <Typography
          variant="h6"
          color="secondary.main"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <CreditCardIcon sx={{ mr: 1 }} /> Extrato de Crédito
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Histórico detalhado de movimentações
        </Typography>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Erro ao carregar o extrato. Tente novamente.
          </Alert>
        ) : transactions.length === 0 ? (
          <Box sx={{ textAlign: "center", p: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Nenhuma transação encontrada.
            </Typography>
          </Box>
        ) : isMobile ? (
          <TransactionsCards transactions={transactions} />
        ) : (
          <TransactionsTable transactions={transactions} />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={onClose} variant="outlined" size="large">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionsModal;
